import { createElement, useEffect, useRef } from "react";

import { Modal as BaseModal, type ModalProps as BaseModalProps } from "@shapr3d/frontend-lib";

import { useTracker } from "../hooks/useTracker";

export const MODAL_NAMES = {
  add_seat_error_grace_period: "add_seat_error_grace_period",
  add_seat_error_individual_sub: "add_seat_error_individual_sub",
  add_seat_error_no_auto_renew: "add_seat_error_no_auto_renew",
  add_seats: "add_seats",
  add_to_space: "add_to_space",
  ar_not_supported: "ar_not_supported",
  billing_details_update: "billing_details_update",
  cancel_subscription: "cancel_subscription",
  cancellation_survey: "cancellation_survey",
  chargebee_checkout_apple_pay_confirmation: "chargebee_checkout_apple_pay_confirmation",
  checkout_error_already_subscribed: "checkout_error_already_subscribed",
  checkout_error_not_allowed: "checkout_error_not_allowed",
  checkout_error_tier_unavailable: "checkout_error_tier_unavailable",
  checkout_error_unknown: "checkout_error_unknown",
  checkout_error_unsupported_country: "checkout_error_unsupported_country",
  checkout_error_unverified_email: "checkout_error_unverified_email",
  checkout_form_error: "checkout_form_error",
  deeplink: "deeplink",
  delete_account: "delete_account",
  delete_comment: "delete_comment",
  edu_request: "edu_request",
  email_change: "email_change",
  individual_subscription_payment_update_error: "individual_subscription_payment_update_error",
  individual_subscription_payment_update_finished:
    "individual_subscription_payment_update_finished",
  individual_subscription_payment_update_success: "individual_subscription_payment_update_success",
  invite_error_grace_period: "invite_error_grace_period",
  invite_error_not_team_owner: "invite_error_not_team_owner",
  link_personal_account: "link_personal_account",
  login: "login",
  name_change: "name_change",
  non_auto_renewing_upgrade: "non_auto_renewing_upgrade",
  password_change: "password_change",
  payment_update_apple_pay_confirmation: "payment_update_apple_pay_confirmation",
  payment_update: "payment_update",
  paywall: "paywall",
  project_page_feature_tour: "project_page_feature_tour",
  publish_version_modal: "publish_version_modal",
  qr_code: "qr_code",
  remove_access_request: "remove_access_request",
  remove_from_space: "remove_from_space",
  remove_project_invitation: "remove_project_invitation",
  remove_project_user: "remove_project_user",
  remove_view_link_invitation: "remove_view_link_invitation",
  remove_view_link_user: "remove_view_link_user",
  rename_version: "rename_version",
  seat_based_subscription_cancel_seats: "seat_based_subscription_cancel_seats",
  seat_based_subscription_chargebee_portal: "seat_based_subscription_chargebee_portal",
  share_project: "share_project",
  share_view_link: "share_view_link",
  space_creation: "space_creation",
  space_name_change: "space_name_change",
  subscription_chargebee_portal: "subscription_chargebee_portal",
  subscription_upgrade: "subscription_upgrade",
  team_invite: "team_invite",
  team_members_invite_cancelled: "team_members_invite_cancelled",
  team_members_remove_member: "team_members_remove_member",
  team_name_change: "team_name_change",
  teams_layout_intent_blocking: "teams_layout_intent_blocking",
  tier_upgrade: "tier_upgrade",
  two_factor_disable: "two_factor_disable",
  two_factor_enable_error: "two_factor_enable_error",
  two_factor_enable_intent: "two_factor_enable_intent",
  two_factor_recovery_code: "two_factor_recovery_code",
  two_factor_setup_success: "two_factor_setup_success",
  unpublish_confirmation: "unpublish_confirmation",
  unpublish: "unpublish",
  welcome: "welcome",
  payment_update_google_pay_confirmation: "payment_update_google_pay_confirmation",
  chargebee_checkout_google_pay_confirmation: "chargebee_checkout_google_pay_confirmation",
} as const;

export type ModalName = (typeof MODAL_NAMES)[keyof typeof MODAL_NAMES];

export type ModalProps = BaseModalProps & { telemetryName: ModalName };

export function Modal({ telemetryName, open, ...rest }: ModalProps) {
  const tracker = useTracker();
  const trackerRef = useRef(tracker);
  const nameRef = useRef(telemetryName);
  useEffect(() => {
    trackerRef.current = tracker;
    nameRef.current = telemetryName;
  });
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const prev = prevOpenRef.current;
    prevOpenRef.current = open;
    if (prev === open) return;
    if (open) {
      trackerRef.current?.trackModalShown(nameRef.current);
    } else {
      trackerRef.current?.trackModalClosed(nameRef.current);
    }
  }, [open]);

  // Strict Mode runs setup→cleanup→setup synchronously. A spurious trackModalClosed
  // fires during the test-unmount cleanup because prevOpenRef is already true.
  // Defer via queueMicrotask: the remount setup resets the flag before it fires.
  const pendingCloseRef = useRef(false);
  useEffect(() => {
    pendingCloseRef.current = false; // cancel any deferred close from a previous cleanup
    return () => {
      if (prevOpenRef.current) {
        pendingCloseRef.current = true;
        queueMicrotask(() => {
          if (pendingCloseRef.current) {
            pendingCloseRef.current = false;
            trackerRef.current?.trackModalClosed(nameRef.current);
          }
        });
      }
    };
  }, []);

  return createElement(BaseModal, { open, ...rest });
}
